import React, { useState, useEffect } from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import {
    Box,
    TextField,
    Button,
    Typography,
    CircularProgress,
    Paper,
} from "@mui/material";
import MediaViewer from "../components/mediaviewer";

const LogNotes = ({ eventId }) => {
    const [note, setNote] = useState("");
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [imageToView, setImageToView] = useState("");

    // Fetch logs and then manually filter by eventId (ExpenseID)
    const fetchLogs = async () => {
        if (!eventId) return;
        setLoading(true);
        setError(null);
        try {
            // Get all logs from the API
            const response = await axios.get(
                "https://figazuresql.swancreate.com/api/swa/FINANCE_Log"
            );
            const allLogs = response.data;
            // Filter logs to only include those matching the eventId
            const filteredLogs = allLogs.filter((log) => log.eventid === eventId);
            console.log("Filtered logs for eventId", eventId, ":", filteredLogs);
            // Sort logs by CreatedOn descending (most recent at the top)
            filteredLogs.sort(
                (a, b) => new Date(b.CreatedOn) - new Date(a.CreatedOn)
            );
            setLogs(filteredLogs);
        } catch (err) {
            setError("Failed to load logs.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [eventId]);

    /**
     * Upload image via POST to the /upload endpoint.
     * The server expects a multipart/form-data body with two fields:
     * - "file": the image file (renamed to a unique GUID with the original file extension)
     * - "folder": the folder name (e.g., "lognotes")
     * On success, the final URL is:
     *   https://media.swancreate.com/images/{folderName}/{uniqueFileName}
     */
    const uploadImage = async (file) => {
        const folderName = "lognotes";
        const ext = file.name.substring(file.name.lastIndexOf("."));
        // Generate a unique file name using a GUID with the original extension only
        const uniqueName = `${uuidv4()}${ext}`;
        // Create a new File object with the unique name
        const newFile = new File([file], uniqueName, { type: file.type });
        const formData = new FormData();
        formData.append("folder", folderName);
        formData.append("file", newFile);
        try {
            const response = await axios.post(
                "https://media.swancreate.com/upload",
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );
            console.log("Image upload response:", response.data);
            // Build final URL using the full API path
            const finalUrl = `https://media.swancreate.com/images/${folderName}/${encodeURIComponent(uniqueName)}`;
            return finalUrl;
        } catch (err) {
            console.error("Upload image error:", err);
            throw new Error("Image upload failed.");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedImageFile && !note.trim()) {
            setError("When attaching an image, you must add a note.");
            return;
        }
        if (!note.trim()) return;
        setSubmitting(true);
        setError(null);
        try {
            let imageUrl = "";
            if (selectedImageFile) {
                imageUrl = await uploadImage(selectedImageFile);
            }
            // Using "ImageUrl" (capital I) to match your DB field.
            await axios.post(
                "https://figazuresql.swancreate.com/api/swa/FINANCE_Log",
                {
                    eventid: eventId,
                    Note: note,
                    Type: "Note",
                    ImageUrl: imageUrl,
                }
            );
            setNote("");
            setSelectedImageFile(null);
            fetchLogs();
        } catch (err) {
            setError("Failed to submit note.");
        } finally {
            setSubmitting(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleViewImage = (url) => {
        console.log("Viewing image:", url);
        setImageToView(url);
        setImageViewerOpen(true);
    };

    return (
        <Box sx={{ maxWidth: "900px", margin: "0 auto", p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Log Notes
                </Typography>
                <Button variant="contained" onClick={handlePrint} sx={{ border: "1px solid #ccc" }}>
                    Print
                </Button>
            </Box>
            <form onSubmit={handleSubmit}>
                <TextField
                    label="Enter Note"
                    multiline
                    fullWidth
                    rows={4}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    variant="outlined"
                    sx={{ mb: 2 }}
                />
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <Button variant="contained" component="label" sx={{ border: "1px solid #ccc", flex: 1 }}>
                        Attach Image
                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                    setSelectedImageFile(e.target.files[0]);
                                }
                            }}
                        />
                    </Button>
                    {selectedImageFile && (
                        <Typography variant="body2" sx={{ alignSelf: "center" }}>
                            {selectedImageFile.name}
                        </Typography>
                    )}
                </Box>
                <Button type="submit" variant="contained" disabled={submitting} sx={{ border: "1px solid #ccc", width: "100%" }}>
                    {submitting ? "Submitting..." : "Add Note / Image"}
                </Button>
            </form>
            {error && (
                <Typography color="error" sx={{ mt: 2 }}>
                    {error}
                </Typography>
            )}
            <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Notes Log
                </Typography>
                {loading ? (
                    <CircularProgress />
                ) : logs.length > 0 ? (
                    logs.map((log, index) => (
                        <Paper
                            key={log.apiid}
                            sx={{
                                p: 2,
                                mb: 2,
                                backgroundColor: index % 2 === 0 ? "rgba(33,33,33,0.9)" : "rgba(33,33,33,0.7)",
                                color: "white",
                            }}
                        >
                            <Typography variant="body1" sx={{ fontSize: "1rem", fontWeight: "bold", color: "#99FFFF" }}>
                                {log.Note}
                            </Typography>
                            {log.ImageUrl &&
                                log.ImageUrl.trim() !== "" &&
                                log.ImageUrl.toLowerCase() !== "null" && (
                                    <Button
                                        variant="contained"
                                        sx={{
                                            mt: 1,
                                            backgroundColor: "green",
                                            color: "white",
                                            border: "1px solid white",
                                            "&:hover": {
                                                backgroundColor: "darkgreen",
                                            },
                                        }}
                                        onClick={() => handleViewImage(log.ImageUrl)}
                                    >
                                        View Image
                                    </Button>
                                )}
                            <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
                                {new Date(log.CreatedOn).toLocaleString()}
                            </Typography>
                        </Paper>
                    ))
                ) : (
                    <Typography>No logs available.</Typography>
                )}
            </Box>
            <MediaViewer
                open={imageViewerOpen}
                onClose={() => setImageViewerOpen(false)}
                src={imageToView}
                type="image"
            />
        </Box>
    );
};

export default LogNotes;
